import React from 'react';
import { ChevronLeft, ChevronRight, Check, AlertCircle, Loader2, SkipForward } from 'lucide-react';
import type { SaveState } from './useRatingAutosave';
import {
  RATING_SCALE,
  MIN_REMARKS_LENGTH,
  SCALE_HINTS,
  RATING_LABELS,
  RATING_CONTENT_MAX_WIDTH,
} from './constants';


interface RatingBarProps {
  rating: number | null;
  remarks: string;
  onRatingChange: (value: number) => void;
  onRemarksChange: (value: string) => void;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
  canGoPrev: boolean;
  isLast: boolean;
  busy: boolean;
  saveState: SaveState;
  error: string | null;
}

function SaveIndicator({ saveState, error }: { saveState: SaveState; error: string | null }) {
  if (saveState === 'saving') {
    return (
      <span className="flex items-center gap-1 text-xs text-gray-500">
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Saving…
      </span>
    );
  }
  if (saveState === 'saved') {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-blue-700">
        <Check className="w-3 h-3" aria-hidden="true" /> Saved
      </span>
    );
  }
  if (saveState === 'error') {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-red-600" role="alert">
        <AlertCircle className="w-3 h-3" aria-hidden="true" /> {error ?? 'Not saved'}
      </span>
    );
  }
  return null;
}

const RatingBar: React.FC<RatingBarProps> = ({
  rating, remarks, onRatingChange, onRemarksChange,
  onPrev, onNext, onSkip, canGoPrev, isLast, busy, saveState, error,
}) => {
  const remarksTooShort = remarks.trim().length < MIN_REMARKS_LENGTH;
  // Skip is offered only when the rater is NOT scoring (no rating) but HAS written a reason.
  // Picking a score means it's a rating, not a skip — keeping the two actions unambiguous.
  const canSkip = rating === null && !remarksTooShort;

  return (
    <div className="fixed bottom-0 left-0 right-0 md:left-16 z-40 bg-white/97 backdrop-blur
                    border-t border-gray-200 shadow-[0_-6px_22px_rgba(15,30,28,0.07)]">
      <div className={`${RATING_CONTENT_MAX_WIDTH} mx-auto px-4 md:px-8 py-3
                      flex flex-col md:flex-row md:items-start gap-4 md:gap-5`}>
        <fieldset className="shrink-0">
          <legend className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Your rating
          </legend>
          <div className="flex gap-1.5 flex-wrap" role="radiogroup" aria-label="Quality rating from 1 to 5">
            {RATING_SCALE.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={rating === value}
                aria-label={`${value} — ${SCALE_HINTS[value]}`}
                title={SCALE_HINTS[value]}
                onClick={() => onRatingChange(value)}
                disabled={busy}
                className={`min-w-[68px] min-h-[44px] px-2 py-1 rounded-xl border-[1.5px]
                            flex flex-col items-center justify-center gap-0.5
                            transition-all hover:-translate-y-px focus:outline-none
                            disabled:opacity-60 focus:ring-2 focus:ring-blue-500
                            focus:ring-offset-1 ${
                  rating === value
                    ? 'bg-gray-900 border-gray-900 text-white'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-blue-600'
                }`}
              >
                <span className="text-[15px] font-semibold leading-none">{value}</span>
                <span className="text-[9px] leading-none whitespace-nowrap">
                  {RATING_LABELS[value]}
                </span>
              </button>
            ))}
          </div>
        </fieldset>

        <div className="flex-1 min-w-0">
          <label
            htmlFor="rating-remarks"
            className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5"
          >
            Why this score? — or why you're skipping <span className="text-red-600">*</span>
          </label>
          <textarea
            id="rating-remarks"
            value={remarks}
            onChange={(e) => onRemarksChange(e.target.value)}
            placeholder="Explain your reasoning — what the output got right or wrong given what was asked…"
            className="w-full min-h-[42px] rounded-xl border-[1.5px] border-gray-200 px-3 py-2
                       text-[13px] resize-y focus:outline-none focus:border-blue-600"
            rows={2}
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Autosaves as you type · saved automatically when you press Next
          </p>
          {/* Reason-driven skip: no standalone Skip button. If the rater can't score this item,
              they write why here and a Skip-with-reason action appears (see the bottom bar). The
              reason is recorded and the item is closed out, so it never comes back to anyone. */}
          {rating === null && (
            <p className="text-[11px] text-gray-500 mt-1.5 flex items-start gap-1">
              <SkipForward className="w-3 h-3 mt-[1px] shrink-0" aria-hidden="true" />
              {canSkip
                ? <span>No score picked — you can <b>Skip with reason</b> instead (button on the right). Your note is saved and this item is closed out.</span>
                : <span>Don’t want to rate this one? Write why above and a <b>Skip with reason</b> button appears — that closes it out (you won’t see it again).</span>}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 md:ml-auto shrink-0 flex-wrap">
          <SaveIndicator saveState={saveState} error={error} />
          {canSkip && (
            <button
              type="button"
              onClick={onSkip}
              disabled={busy}
              title="Record your reason and close this item out — you won't see it again"
              className="px-4 py-2 min-h-[44px] rounded-xl border-[1.5px] border-gray-300 bg-white
                         text-[13px] font-semibold text-gray-700 hover:border-gray-900
                         hover:text-gray-900 disabled:opacity-50 flex items-center gap-1"
            >
              <SkipForward className="w-3.5 h-3.5" aria-hidden="true" /> Skip with reason
            </button>
          )}
          <button
            type="button"
            onClick={onPrev}
            disabled={!canGoPrev || busy}
            className="px-4 py-2 min-h-[44px] rounded-xl border-[1.5px] border-gray-200 bg-white
                       text-[13px] font-semibold disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Prev
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={busy || rating === null || remarksTooShort}
            title={
              rating === null
                ? 'Pick a score first'
                : remarksTooShort
                  ? `Add at least ${MIN_REMARKS_LENGTH} characters explaining the score`
                  : undefined
            }
            className="px-4 py-2 min-h-[44px] rounded-xl bg-gray-900 text-white text-[13px]
                       font-semibold hover:bg-gray-800 disabled:opacity-40
                       flex items-center gap-1"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
            {isLast ? 'Save & get next' : 'Next'}
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RatingBar;
